import { Injectable, Inject } from '@angular/core';
import { HttpClient } from '@angular/common/http';

import { BehaviorSubject, Observable, of, Subject } from 'rxjs';
import { switchMap, tap, map } from 'rxjs/operators';
import { SortColumn, SortDirection } from '../_directives/sortable.directive';
import { State } from '../_models/State';
import { ItemImages, Items } from '../_models/Items';
import { Category } from '../_models/Cateogry';
import { Subcategory } from '../_models/Subcategory';
import { City } from '../_models/City';

interface SearchItemsResult {
  data: Items[];
  total: number;
}
const compare = (v1: string, v2: string) => v1 < v2 ? -1 : v1 > v2 ? 1 : 0;

function sort(data: Items[], column: SortColumn, direction: string): Items[] {
  if (direction === '' || column === '') {
    return data;
  } else {
    return [...data].sort((a, b) => {
      const res = compare(`${a[column]}`, `${b[column]}`);
      return direction === 'asc' ? res : -res;
    });
  }
}

function matches(data: Items, term: string) {
  return data.title.toLowerCase().includes(term.toLowerCase());
}

@Injectable({
  providedIn: 'root'
})

export class ItemsService {

  constructor(private http: HttpClient) {
  }

  private _loading$ = new BehaviorSubject<boolean>(true);
  private _search$ = new Subject<void>();
  private _allData$ = new BehaviorSubject<Items[]>([]);
  private _data$ = new BehaviorSubject<Items[]>([]);
  private _total$ = new BehaviorSubject<number>(0);
  public items: Items[];
  private _state: State = {
    page: 1,
    pageSize: 10,
    searchTerm: '',
    sortColumn: '',
    sortDirection: ''
  };

  get total$() { return this._total$.asObservable(); }
  get loading$() { return this._loading$.asObservable(); }
  get page() { return this._state.page; }
  get pageSize() { return this._state.pageSize; }
  get searchTerm() { return this._state.searchTerm; }

  /** 👇 Expose total as a plain number for template helpers */
  get total() { return this._total$.value; }

  set page(page: number) { this._set({ page }); }
  set pageSize(pageSize: number) { this._set({ pageSize }); }

  /** 👇 Reset to page 1 when filter changes */
  set searchTerm(searchTerm: any) {
    this._state.page = 1;
    this._set({ searchTerm });
  }

  /** 👇 Reset to page 1 when sort changes */
  set sortColumn(sortColumn: SortColumn) {
    this._state.page = 1;
    this._set({ sortColumn });
  }

  set sortDirection(sortDirection: SortDirection) {
    this._state.page = 1;
    this._set({ sortDirection });
  }

  get data$() {
    return this._data$.asObservable();
  }
  get allData$() {
    return this._allData$.asObservable();
  }
  loadCities() {
    return this.http.get<City[]>(`api/city/all`);
  }
  loadCategories() {
    return this.http.get<Category[]>(`api/category/all`);
  }
  loadItemImages(id) {
    return this.http.get<ItemImages[]>(`api/item/images/${id}`);
  }
  loadSubCategories() {
    return this.http.get<Subcategory[]>(`api/subcategory/all`);
  }
  getById(id) {
    return this.http.get<Items[]>(`api/item/${id}`);
  }

  getAllData() {

    const url = `api/item/all`;
    console.log(url);
    tap(() => this._loading$.next(true)),
      this.http.get<Items[]>(url).subscribe(res => {
        this.items = res;

        this._data$.next(this.items);
        this._allData$.next(this.items);

        this._search$.pipe(
          switchMap(() => this._search()),
          tap(() => this._loading$.next(false))
        ).subscribe(result => {
          this._data$.next(result.data);
          this._total$.next(result.total);
        });

        this._search$.next();
      });
  }

  private _set(patch: Partial<State>) {
    Object.assign(this._state, patch);
    this._search$.next();
  }

  private _search(): Observable<SearchItemsResult> {
    const { sortColumn, sortDirection, pageSize, page, searchTerm } = this._state;

    // 1. sort
    let sortedData = sort(this.items, sortColumn, sortDirection);

    // 2. filter
    sortedData = sortedData.filter(data => matches(data, searchTerm));
    const total = sortedData.length;

    // 3. paginate
    const data = sortedData.slice((page - 1) * pageSize, (page - 1) * pageSize + pageSize);
    return of({ data, total });
  }

  clear() {
    this._search$.next();
    this._data$.next(null);
    this._allData$.next(null);
    this._total$.next(null);
    this._loading$.next(null);
    this._state = {
      page: 1,
      pageSize: 40,
      searchTerm: '',
      sortColumn: '',
      sortDirection: ''
    };
  }

  insert(data) {
    return this.http.post(`api/item/insert`, data)
      .pipe(map(res => {
        console.log(res);
        return res;
      }));
  }

  update(updateData) {
    return this.http.post(`api/item/update`, updateData)
      .pipe(map(res => {
        console.log(res);
        return res;
      }));
  }

  delete(updateData) {
    return this.http.post(`api/item/delete`, updateData);
  }
}